<template>
    <div class="ui-page">
        <ui-page-header :title="judge ? judge.name : 'Carregando...'" back="/prospeccao/juizes" back-text="Juízes e varas">
            <template #title-append>
                <ui-badge v-if="judge" :status="levelBadge(judge.level)" :text="judge.level_label" />
                <ui-badge v-if="judge && !judge.active" status="inactive" text="inativo" class="ml-1" />
            </template>
            <template #subtitle>
                <span v-if="judge">Juiz(a) de Direito · {{ judge.tribunal || "tribunal não informado" }} · {{ judge.cases.length }} {{ judge.cases.length === 1 ? "caso captado" : "casos captados" }}</span>
            </template>
            <template v-if="judge">
                <v-btn v-if="$allowed('create')" outlined @click="openAssignment"><v-icon left small>mdi-map-marker-plus-outline</v-icon>Registrar nova lotação</v-btn>
                <v-btn v-if="$allowed('edit')" color="primary" depressed @click="openEdit"><v-icon left small>mdi-pencil-outline</v-icon>Editar cadastro</v-btn>
            </template>
        </ui-page-header>

        <div v-if="judge" class="pjd-grid">
            <div>
                <ui-section title="Relacionamento BeJ (todas as varas)" description="Agendas, onepages, feedbacks e nomeações nos casos deste juiz.">
                    <ui-empty v-if="!judge.timeline.length" text="Nenhum contato registrado com este juiz." />
                    <ol v-else class="pjd-timeline">
                        <li v-for="item in judge.timeline" :key="item.id">
                            <span class="pjd-timeline__date">{{ formatFeedDate(item.occurred_at) }}</span>
                            <div>
                                <div><strong>{{ item.type_label }}</strong> · <router-link :to="`/prospeccao/casos/${item.case_id}`">{{ item.case_name }}</router-link><span v-if="item.admin_name" class="ui-muted"> · {{ item.admin_name }}</span></div>
                                <div>{{ item.description }}</div>
                                <div v-if="item.feedback" class="ui-small"><strong>Feedback:</strong> {{ item.feedback }}</div>
                            </div>
                        </li>
                    </ol>
                </ui-section>

                <ui-section title="Casos com este juiz" class="mt-4">
                    <ui-empty v-if="!judge.cases.length" text="Nenhum caso." />
                    <table v-else class="pjd-table">
                        <tbody>
                            <tr v-for="item in judge.cases" :key="item.id">
                                <td><router-link :to="`/prospeccao/casos/${item.id}`"><strong>{{ item.company_name }}</strong></router-link><div class="ui-small ui-muted ui-mono">{{ item.process_number }}</div></td>
                                <td>{{ item.type === "tca" ? "TCA" : "RJ" }} · {{ item.uf || "—" }}</td>
                                <td>{{ item.court_name || "—" }}</td>
                                <td><ui-badge set="prospect-pipeline" :status="item.pipeline_status" /></td>
                            </tr>
                        </tbody>
                    </table>
                </ui-section>
            </div>

            <div>
                <ui-section title="Cadastro">
                    <dl class="pjd-dl">
                        <dt>Contato pessoal</dt>
                        <dd :class="{ 'ui-muted': !judge.personal_email && !judge.personal_phone }">{{ [judge.personal_email, judge.personal_phone].filter(Boolean).join(" · ") || "a preencher" }}<div class="ui-small ui-muted">segue o juiz, não a vara</div></dd>
                        <dt>Provimento 231</dt><dd>{{ prov231Label(judge.follows_prov231) }}</dd>
                        <dt>Perfil</dt><dd :class="{ 'ui-muted': !judge.profile_notes }" style="white-space: pre-line;">{{ judge.profile_notes || "a preencher" }}</dd>
                        <dt>AJs que nomeou</dt>
                        <dd>
                            <span v-if="!judge.appointed.length" class="ui-muted">—</span>
                            <div v-for="entry in judge.appointed" :key="`${entry.administrator.id}-${entry.role}`">{{ entry.administrator.name }}{{ entry.role === "pp" ? " (PP)" : "" }} · {{ entry.count }}</div>
                        </dd>
                    </dl>
                </ui-section>

                <ui-section title="Histórico de lotações" class="mt-4">
                    <ui-empty v-if="!judge.assignments.length" text="Nenhuma lotação registrada." />
                    <ol v-else class="pjd-timeline">
                        <li v-for="assignment in judge.assignments" :key="assignment.id">
                            <span class="pjd-timeline__date">{{ assignment.current ? "atual" : `até ${formatFeedDate(assignment.ended_at)}` }}</span>
                            <div>
                                <strong>{{ assignment.court ? assignment.court.name : "—" }}</strong>
                                <span v-if="assignment.started_at" class="ui-muted"> · desde {{ formatFeedDate(assignment.started_at) }}</span>
                                <div class="ui-small ui-muted">{{ assignment.source === "manual" ? "informada pela equipe" : "detectada na captação" }}</div>
                                <div v-if="assignment.status === 'to_confirm'" class="mt-1">
                                    <ui-badge status="pending" text="a confirmar" />
                                    <v-btn v-if="$allowed('edit')" x-small text :disabled="busy" @click="confirmAssignment(assignment)">Confirmar</v-btn>
                                </div>
                            </div>
                        </li>
                    </ol>
                </ui-section>

                <ui-section title="Feedback das agendas" class="mt-4">
                    <ui-empty v-if="!judge.feedbacks.length" text="Nenhum feedback registrado." />
                    <div v-for="item in judge.feedbacks" :key="`fb-${item.id}`" class="pjd-feedback">
                        <span class="ui-muted">[{{ formatFeedDate(item.occurred_at) }}]</span> {{ item.feedback || item.description }}<span v-if="item.admin_name" class="ui-muted"> · {{ item.admin_name }}</span>
                    </div>
                </ui-section>
            </div>
        </div>

        <v-dialog v-model="edit.open" max-width="560" persistent>
            <v-card class="ui-card">
                <div class="ui-card__header">
                    <div>
                        <h3 class="ui-section-title">Editar cadastro do juiz</h3>
                        <p class="ui-section-description">Contatos pessoais são dados pessoais: registre só o necessário para o relacionamento.</p>
                    </div>
                </div>
                <div class="ui-card__body">
                    <v-row dense>
                        <v-col cols="12" sm="8"><v-text-field v-model="edit.form.name" label="Nome" outlined dense hide-details="auto"></v-text-field></v-col>
                        <v-col cols="12" sm="4"><v-text-field v-model="edit.form.tribunal" label="Tribunal" outlined dense hide-details="auto"></v-text-field></v-col>
                        <v-col cols="12" sm="7"><v-text-field v-model="edit.form.personal_email" label="E-mail" outlined dense hide-details="auto"></v-text-field></v-col>
                        <v-col cols="12" sm="5"><v-text-field v-model="edit.form.personal_phone" label="Telefone" outlined dense hide-details="auto"></v-text-field></v-col>
                        <v-col cols="12"><v-select v-model="edit.form.follows_prov231" :items="prov231Items" label="Segue o Provimento 231/2026" outlined dense hide-details="auto" :menu-props="{ offsetY: true }"></v-select></v-col>
                        <v-col cols="12"><v-textarea v-model="edit.form.profile_notes" label="Perfil (como decide, o que valoriza)" outlined dense rows="3" auto-grow hide-details="auto"></v-textarea></v-col>
                    </v-row>
                    <p class="ui-small ui-muted mt-2 mb-0">Trocar o nome não perde o vínculo: a importação continua reconhecendo o nome antigo.</p>
                    <v-switch v-model="edit.form.active" inset dense hide-details class="mt-3"
                        :label="edit.form.active ? 'Ativo' : 'Inativo (aposentado ou fora da jurisdição): sai da lista, mas o histórico fica'"></v-switch>
                </div>
                <div class="ui-card__footer">
                    <v-btn outlined :disabled="edit.saving" @click="edit.open = false">Cancelar</v-btn>
                    <v-btn v-if="$allowed('save')" color="primary" depressed :loading="edit.saving" @click="saveEdit">Salvar</v-btn>
                </div>
            </v-card>
        </v-dialog>

        <v-dialog v-model="assignment.open" max-width="560" persistent>
            <v-card class="ui-card">
                <div class="ui-card__header">
                    <div>
                        <h3 class="ui-section-title">Registrar lotação</h3>
                        <p class="ui-section-description">Sem data de fim, vira a lotação atual: encerra a lotação atual do juiz e a do titular atual da vara.</p>
                    </div>
                </div>
                <div class="ui-card__body">
                    <v-autocomplete v-model="assignment.court_id" :items="courts" item-value="id" :item-text="courtText" label="Vara" outlined dense hide-details="auto" class="mb-3"
                        no-data-text="Nenhuma vara encontrada"></v-autocomplete>
                    <v-row dense>
                        <v-col cols="6"><v-text-field v-model="assignment.started_at" type="date" label="Início" outlined dense hide-details="auto"></v-text-field></v-col>
                        <v-col cols="6"><v-text-field v-model="assignment.ended_at" type="date" label="Fim (se já encerrada)" outlined dense hide-details="auto"></v-text-field></v-col>
                    </v-row>
                    <p class="ui-small ui-muted mt-3 mb-0">Não achou a vara? <a href="#" @click.prevent="courtDialog = true">Cadastrar nova vara</a></p>
                </div>
                <div class="ui-card__footer">
                    <v-btn outlined :disabled="assignment.saving" @click="assignment.open = false">Cancelar</v-btn>
                    <v-btn v-if="$allowed('create')" color="primary" depressed :loading="assignment.saving" :disabled="!assignment.court_id" @click="saveAssignment">Registrar</v-btn>
                </div>
            </v-card>
        </v-dialog>

        <court-form-dialog v-model="courtDialog" @saved="afterCourtCreate" />
    </div>
</template>

<script>
import axios from "axios";
import CourtFormDialog from "@/components/prospect/bench/CourtFormDialog.vue";
import { formatFeedDate, notifyError, notifySuccess } from "@/components/prospect/prospect.util";

const PROV231 = [{ value: "a_confirmar", text: "A confirmar" }, { value: "sim", text: "Sim" }, { value: "nao", text: "Não" }];

export default {
    name: "ProspectJudgeDetails",

    components: { CourtFormDialog },

    data: () => ({
        judge: null,
        courts: [],
        busy: false,
        courtDialog: false,
        prov231Items: PROV231,
        edit: { open: false, saving: false, form: {} },
        assignment: { open: false, saving: false, court_id: null, started_at: "", ended_at: "" },
    }),

    watch: {
        "$route.params.id"() {
            this.load();
        },
    },

    created() {
        this.load();
    },

    methods: {
        formatFeedDate,

        levelBadge(level) {
            return { nomeou_bej: "approved", agenda_feita: "active", contato: "pending" }[level] || "inactive";
        },

        prov231Label(value) {
            return (PROV231.find((item) => item.value === value) || PROV231[0]).text;
        },

        courtText(court) {
            return `${court.name} (${[court.city, court.uf].filter(Boolean).join("/")})`;
        },

        async load() {
            try {
                const result = await axios.get(`/api/admin/prospect/judges/${this.$route.params.id}`);
                this.judge = result.data.content;
            } catch (error) {
                notifyError(this, error);
            }
        },

        openEdit() {
            const judge = this.judge;
            this.edit = {
                open: true,
                saving: false,
                form: { name: judge.name, tribunal: judge.tribunal || "", active: judge.active !== false, personal_email: judge.personal_email || "", personal_phone: judge.personal_phone || "", follows_prov231: judge.follows_prov231, profile_notes: judge.profile_notes || "" },
            };
        },

        async saveEdit() {
            this.edit.saving = true;
            try {
                const result = await axios.patch(`/api/admin/prospect/judges/${this.judge.id}`, this.edit.form);
                this.judge = result.data.content;
                this.edit.open = false;
                notifySuccess(this, "Cadastro atualizado.");
            } catch (error) {
                notifyError(this, error);
            } finally {
                this.edit.saving = false;
            }
        },

        async openAssignment() {
            this.assignment = { open: true, saving: false, court_id: null, started_at: "", ended_at: "" };
            if (!this.courts.length) {
                try {
                    const result = await axios.get("/api/admin/prospect/courts");
                    this.courts = result.data.content;
                } catch (error) {
                    notifyError(this, error);
                }
            }
        },

        // Vara criada pelo dialogo de lotacao: ja fica escolhida.
        afterCourtCreate(court) {
            this.courts = [...this.courts, court];
            this.assignment.court_id = court.id;
        },

        async saveAssignment() {
            this.assignment.saving = true;
            try {
                const { court_id: courtId, started_at: startedAt, ended_at: endedAt } = this.assignment;
                const result = await axios.post(`/api/admin/prospect/judges/${this.judge.id}/assignments`, { court_id: courtId, started_at: startedAt || null, ended_at: endedAt || null });
                this.judge = result.data.content;
                this.assignment.open = false;
                notifySuccess(this, "Lotação registrada.");
            } catch (error) {
                notifyError(this, error);
            } finally {
                this.assignment.saving = false;
            }
        },

        async confirmAssignment(assignment) {
            this.busy = true;
            try {
                const result = await axios.post(`/api/admin/prospect/assignments/${assignment.id}/confirm`);
                this.judge = result.data.content;
                notifySuccess(this, "Lotação confirmada.");
            } catch (error) {
                notifyError(this, error);
            } finally {
                this.busy = false;
            }
        },
    },
};
</script>

<style lang="scss" scoped>
@import "../../assets/scss/ui/tokens";

.pjd-grid {
    display: grid;
    grid-template-columns: minmax(0, 1.5fr) minmax(0, 1fr);
    gap: 16px;
    align-items: start;

    @media (max-width: 1100px) {
        grid-template-columns: minmax(0, 1fr);
    }
}

// Cartoes lado a lado: sem o espacamento vertical de ".ui-card + .ui-card".
.pjd-grid > .ui-card + .ui-card {
    margin-top: 0;
}

.pjd-timeline {
    list-style: none;
    margin: 0;
    padding: 0;
    font-size: 13px;

    li {
        display: grid;
        grid-template-columns: 90px minmax(0, 1fr);
        gap: 12px;
        padding: 10px 0;
        border-bottom: 1px solid $ui-border-soft;

        &:last-child {
            border-bottom: 0;
        }
    }
}

.pjd-timeline__date {
    color: $ui-muted;
}

.pjd-dl {
    display: grid;
    grid-template-columns: 120px minmax(0, 1fr);
    gap: 8px 12px;
    margin: 0;
    font-size: 13px;

    dt {
        color: $ui-muted;
    }

    dd {
        margin: 0;
    }
}

.pjd-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;

    td {
        padding: 8px 6px;
        border-bottom: 1px solid $ui-border-soft;
        vertical-align: top;
    }
}

.pjd-feedback {
    padding: 6px 0;
    font-size: 13px;
}
</style>
